import { html, TemplateResult } from "lit";
import { connectedShadowParts, loginShadowParts, frameShadowParts } from '../../constants/shadow-parts'
import type { CSSResultGroup } from "lit";
import { property, query } from "lit/decorators.js";
import { PropertyValues } from "lit";
import { state } from "lit/decorators/state.js";
import "@pushengineering/push-web-components/overlay";
import "@pushengineering/push-web-components/button";
import "@pushengineering/push-web-components/header-drawer";
import "@pushengineering/push-web-components/horizontal-stack";
import fanPassUnlockStyles from "./fan-pass-unlock.styles";
import type Button from "@pushengineering/push-web-components/button";
import type Overlay from "@pushengineering/push-web-components/overlay";

import { N3xusAPIController } from "../../controllers/N3xusAPIController";
import CirkayElement from "../../internal/CirkayElement.js";
import { UnlockEvents } from "../../events/unlock";
import {
  addSession,
  getLatestSession,
  preloadLogos,
  LogoSet,
  FpSession,
  clearStoredSession,
  findBoxNode,
} from "../../utils/helpers";
import { UnlockStateTypes, LoginState } from "./types";
import { FanPassUser } from "../../controllers/types";
import { LoginController } from "../../controllers/LoginController";
import "../login";
import "../frame";
import "../error-screen";
import "../connected";
import { MagicOptions } from "../../base/magic/MagicAPI";
import { LoginStateTypes } from "../login/types";
import { AccessCheckData, N3xusApiConfig, Environment } from "../../base/n3xus/types";
import { LoginEvents } from "../../events/login";

import type Login from "../login";

/**
 * A web component for Fan Pass unlock.
 *
 * @fires fp-unlock-initialising - Dispatched when the component is initializing.
 * @fires fp-unlock-initialised - Dispatched when the component is initialized and ready for use.
 * @fires fp-unlock-started - Dispatched when the unlock flow starts.
 * @fires fp-unlocked' - Dispatched when the unlock is completed
 */
//@customElement(`${prefix}-login`)
export default class FanPassUnlock extends CirkayElement {
  static styles: CSSResultGroup = [fanPassUnlockStyles];

  private loginController: LoginController | null = null;
  private interval: any = null;
  private walletCheckResponse: AccessCheckData | undefined = undefined;
  private unlockedContent: boolean = false;
  private n3xusAPIController: N3xusAPIController | undefined = undefined;
  private n3xusOptions: N3xusApiConfig | undefined = undefined;
  private isComponentConnected: boolean = false;

  @state()
  _unlockState: UnlockStateTypes = UnlockStateTypes.locked;

  @property({ type: String }) apiKey = "";

  @property({ type: Boolean }) inline = false;

  @property({ type: String }) fanPassTitle = "Clash Pass";

  @property({ type: String }) buttonLabel = "Connect";

  @property({ type: Object }) logoSet: LogoSet = {
    large: {
      src: "https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp",
    },
    medium: {
      src: "https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp",
    },
    small: {
      src: "https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp",
    },
    alt: `${this.fanPassTitle} Logo`,
  };

  @property({ type: String }) logoUrl = "http://cirkay.com";

  @property({ type: Boolean }) loading = false;

  @property({ type: Boolean }) validLoadingState = true;

  @property({ type: Boolean }) unlocked = false;

  @property({ type: String }) environment: Environment = Environment.PRODUCTION;

  @property({ type: Boolean }) debugLogging = false;

  @property({ type: String }) error: string | null = null; // Add error property

  @property({ type: Boolean }) cta = false;

  @property({ type: String }) ctaText: string = "Connect to unlock";

  @property({ type: String }) unlockMetadata: string = "";

  @property({ type: Boolean }) hideUnlockButton = false

  @property({ type: Boolean }) hideConnected = false

  @property({ type: String }) title: string = "${fanPassTitle}";

  @property({ type: String }) connectingText: string = "Connecting...";

  /** Url for custom faq site  */
  @property({ type: String })
  faqUrl: string = "https://cirkay.com/faq/";

  /** The page number for pager dots */
  @state()
  _pageNumber: number = 1;

  @state()
  _user: FanPassUser | undefined = undefined;

  @state()
  _loggingState: LoginState | undefined = LoginState.initialising;

  @query("fp-login")
  _loginComponent: Login | undefined;

  constructor() {
    super();
    const imgApple = new Image();
    imgApple.src =
      "https://cdn.n3xus.services/production/assets/images/US-UK_Add_to_Apple_Wallet_RGB_101421.svg";

    const imgGoogle = new Image();
    imgGoogle.src =
      "https://cdn.n3xus.services/production/assets/images/enGB_add_to_google_wallet_wallet-button.svg";

    const imgCirkay = new Image();
    imgCirkay.src =
      "https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp";
  }

  override connectedCallback() {
    // Prevent multiple initialization
    if (this.isComponentConnected) {
      console.log('🔄 FP-UNLOCK - Already connected, skipping duplicate connectedCallback');
      return;
    }
    
    this.isComponentConnected = true;
    console.log('🔧 FP-UNLOCK - First connectedCallback, initializing...');
    
    try {
      preloadLogos(this.logoSet);
    } catch (error) {
      console.warn("Error loading logo images", error);
    }
    this.loading = true;
    super.connectedCallback();

    if (!this.apiKey) {
      this.error = "API key is required";
      this.loading = false;
      return;
    }

    this.emit(LoginEvents.Initialising, {
      bubbles: true,
      composed: true,
    });

    const magicApiOptions: MagicOptions = {
      environment: this.environment,
    };

    this.n3xusOptions = {
      apiKey: this.apiKey,
      environment: this.environment,
    } as N3xusApiConfig;

    this.n3xusAPIController = N3xusAPIController.getInstance(this.debugLogging);
    this.n3xusAPIController.addHost(this);

    this.loginController = LoginController.getInstance(
      magicApiOptions,
      this.debugLogging
    );

    this.loginController.addHost(this);

    const session = getLatestSession(this.apiKey);
    const minutesAgo = Date.now() - 3.5 * 60 * 1000;

    console.log('session')
    console.log(session)
    if (session && !(session.issueTS < minutesAgo)) {
      this.unlock(session.didToken, session.token, session);
    }

    if (!this.logoSet.alt) {
      this.logoSet.alt = `${this.fanPassTitle} logo`;
    }
    this.loading = false;
  }

  override disconnectedCallback() {
    console.log('🔌 FP-UNLOCK - Disconnecting component');
    super.disconnectedCallback();
    this.n3xusAPIController?.removeHost(this);
    this.loginController?.removeHost(this);
    this.stopPolling();
    this.isComponentConnected = false;
  }

  public async logout() {
    this._unlockState = UnlockStateTypes.locked;
    await this.handleLogout();
  }

  private async handleLogout() {
    this.walletCheckResponse = undefined;
    this._unlockState = UnlockStateTypes.locked; // Important: Reset the unlock state
    this.emit(LoginEvents.Logout, {
      bubbles: true,
      composed: true,
      detail: {
        LoggedIn: false,
      },
    });
    await this.loginController?.logout();
  }

  protected async willUpdate(changedProperties: PropertyValues): Promise<void> {
    if (changedProperties.has("apiKey") || changedProperties.has("environment")) {
      if (!this.apiKey) {
        this.error = "API key is required";
        return;
      }
      this.n3xusOptions = {
        apiKey: this.apiKey,
        environment: this.environment,
      } as N3xusApiConfig;
    }

    const detail = this.loginController?.user
      ? {
          email: this.loginController.user.email ?? null,
          did: this.loginController.user.did?.token ?? null,
          wallet: this.loginController?.user.walletAddress ?? null,
          isLoggedin: this.loginController.loggedIn,

          /*
            sessionToken: sessionToken,
            email: this.loginController?.user?.email ?? session?.email,
            apiKey: this.apiKey ?? session?.apiKey,
            unlocked: true,
            wallet: this.loginController?.user?.walletAddress ??  session?.wallet,
            did: this.loginController?.user?.did?.token,
            expiryDate: this.walletCheckResponse?.data?.expiryDate ?? session?.expiryDate,
          */
        }
      : {
          isLoggedin: this.loginController?.loggedIn,
        };

    if (
      this.loginController?.loginInit &&
      this._loggingState === LoginState.initialising
    ) {
      this.emit(LoginEvents.Initialised, {
        bubbles: true,
        composed: true,
        detail,
      });
    }

    if (this.loginController?.loggedIn && this.loginController?.loginInit) {
      this._loggingState = LoginState.loggedIn;
    } else if (this.loginController?.loginInit) {
      this._loggingState = LoginState.loggedOut;
    }

    if (
      (UnlockStateTypes.unlocked === this._unlockState ||
        UnlockStateTypes.lockedLoggingIn === this._unlockState) &&
      this.loginController?.loginInit &&
      !this.loginController?.loggedIn
    ) {
      this.walletCheckResponse = undefined;
      this.stopPolling();
      this._unlockState = UnlockStateTypes.locked;
    }

    if (
      (UnlockStateTypes.unlocked === this._unlockState &&
        this.loginController?.loginInit &&
        !this.loginController?.loggedIn) ||
      (UnlockStateTypes.lockedLoggingIn === this._unlockState &&
        this.loginController?.loggedIn &&
        this.loginController?.loginInit) ||
      (UnlockStateTypes.lockedLoggingIn === this._unlockState &&
        this.loginController?.loginInit &&
        !this.loginController?.loggedIn)
    ) {
      this.unlockedContent = false;
      this.stopPolling();
      //Dispach logged out event
      this.emit(LoginEvents.Logout, {
        bubbles: true,
        composed: true,
        detail: {
          isLoggedin: false,
        },
      });
    }

    if (
      this.loginController?.loggedIn &&
      this._unlockState === UnlockStateTypes.locked
    ) {
      this.emit(LoginEvents.Login, {
        bubbles: true,
        composed: true,
        detail,
      });
      await this.handleUnlock();
    } else if (!this.interval && this.loginController?.loggedIn) {
      this.startPolling();
      //Still want to call to get mobile wallet state from API
      this.checkSessionStatus();
    }

    if (this.loginController?.loginInit && !this.loginController?.loggedIn) {
      this.unlockedContent = false;
    }
  }

  public getUserDetails(): FanPassUser | undefined | null {
    return this.loginController?.user;
  }

  public getLoginState(): LoginState | undefined {
    return this._loggingState;
  }

  public getUnlockState(): UnlockStateTypes | undefined {
    return this._unlockState;
  }

  private async handleUnlock() {
    this.emit(UnlockEvents.Started, { bubbles: true, composed: true });

    let didToken: string | undefined | null =
      this.loginController?.user?.did?.token;

    if (!didToken) {
      didToken = await this.loginController?.getDidToken();
    }
    if (!didToken || !this.n3xusAPIController) {
      console.error("No did token or api");
      return;
    }
    if (this._unlockState !== UnlockStateTypes.unlocked) {
      this._unlockState = UnlockStateTypes.unlocking;
    }

    // 2. check wallet for fanpass
    // maybe add source id
    if (this.error == null) {
      await this.unlock(didToken);
    }
  }

  private async checkSessionStatus() {
    this.logging("checkSessionStatus");

    // Early return if user is not logged in
    if (!this.loginController?.loggedIn) {
      this.logging("User is not logged in, stopping polling");
      this.stopPolling();
      return;
    }

    const session = getLatestSession(this.apiKey);
    this.logging("session", session);
    const threeMinutesFortyFiveSeconds = 3.5 * 60 * 1000; // 3 minutes 45 seconds in milliseconds
    const minutesAgo = Date.now() - threeMinutesFortyFiveSeconds;
    this.logging("minutesAgo", minutesAgo);

    if (session && session.issueTS > minutesAgo) {
      // Session is still valid, no need to make API call
      this.logging("session is still valid, no need to make API call");
      return;
    }

    this.logging("session is expired, making API call");
    // Only make API call if session is expired or doesn't exist
    const did = await this.loginController?.getDidToken();
    if (did && this.loginController?.loggedIn) {
      this.unlock(did);
    } else {
      clearStoredSession();
    }
  }

  // Start polling every 30 seconds
  private startPolling() {
    if (!this.interval) {
      this.interval = setInterval(() => this.checkSessionStatus(), 30000);
    }
  }

  // Stop polling
  private stopPolling() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  private async unlock(
    didToken: string,
    sessionToken: string | null = null,
    session: FpSession | null = null
  ) {
    // Prevent duplicate unlock calls if already unlocked
    if (this._unlockState === UnlockStateTypes.unlocked && this.unlockedContent) {
      console.log('🔄 FP-UNLOCK - Already unlocked, skipping duplicate unlock call');
      return;
    }
    
    console.log('🔧 FP-UNLOCK - Processing unlock request:', {
      hasSessionToken: !!sessionToken,
      currentState: this._unlockState,
      unlockedContent: this.unlockedContent
    });
    if (!sessionToken) {
      try {
        if (!this.n3xusOptions || !this.n3xusAPIController) {
          throw new Error("N3xus API is not properly initialized");
        }
        this.walletCheckResponse =
          await this.n3xusAPIController.checkWalletForNft(
            this.n3xusOptions,
            this.unlockMetadata,
            didToken
          );
        if (this.walletCheckResponse) {
          const data = this.walletCheckResponse.data;

          if (!data) {
            console.error("No data in wallet check response");
            return;
          }
          
          if (data.holds == true && data.expired == false) {
            this._unlockState = UnlockStateTypes.unlocked;
            addSession({
              token: data.sessionToken,
              issueTS: Date.now(),
              didToken: didToken,
              apiKey: this.apiKey,
              mobilePass: this.walletCheckResponse?.data?.mobilePasses,
              wallet: this.loginController?.user?.walletAddress,
              email: this.loginController?.user?.email,
              expiryDate: this.walletCheckResponse?.data?.expiryDate,
            });

            if (!this.unlockedContent) {
              this.unlockedContent = true;

              this.emit(UnlockEvents.UnlockComplete, {
                bubbles: true,
                composed: true,
                detail: {
                  sessionToken: data.sessionToken,
                  email: this.loginController?.user?.email,
                  wallet: this.loginController?.user?.walletAddress,
                  did: this.loginController?.user?.did?.token,
                  apiKey: this.apiKey,
                  expiryDate: this.walletCheckResponse?.data?.expiryDate,
                  unlocked: true,
                },
              });
            }
            this.emit(UnlockEvents.MobilePassData, {
              bubbles: true,
              composed: true,
              detail: {
                sessionToken: sessionToken,
                email: this.loginController?.user?.email,
                apiKey: this.apiKey,
                wallet: this.loginController?.user?.walletAddress,
                did: this.loginController?.user?.did?.token,
                unlocked: true,
                mobilePass: this.walletCheckResponse.data?.mobilePasses,
              },
            });
          } else {
            this._unlockState = UnlockStateTypes.lockedLoggingIn;
            if (!this.unlockedContent) {
              this.unlockedContent = true;
              this.emit(UnlockEvents.UnlockComplete, {
                bubbles: true,
                composed: true,
                detail: {
                  unlocked: false,
                  email: this.loginController?.user?.email,
                  wallet: this.loginController?.user?.walletAddress,
                  did: this.loginController?.user?.did?.token,
                },
              });
            }
          }
        }
        this.loading = false;
      } catch (err) {
        if (err instanceof Error) {
          this._unlockState = UnlockStateTypes.locked;
          this.error = err.message;
        } else {
          this.error = "An unknown error occurred";
        }
        this._unlockState = UnlockStateTypes.locked;
        await this.handleLogout();
      }
    } else {
      this._unlockState = UnlockStateTypes.unlocked;
      if (!this.unlockedContent) {
        this.unlockedContent = true;
        this.emit(UnlockEvents.UnlockComplete, {
          bubbles: true,
          composed: true,
          detail: {
            sessionToken: sessionToken,
            email: this.loginController?.user?.email ?? session?.email,
            apiKey: this.apiKey ?? session?.apiKey,
            unlocked: true,
            wallet: this.loginController?.user?.walletAddress ??  session?.wallet,
            did: this.loginController?.user?.did?.token,
            expiryDate: this.walletCheckResponse?.data?.expiryDate ?? session?.expiryDate,
          },
        });

        if (session) {
          this.emit(UnlockEvents.MobilePassData, {
            bubbles: true,
            composed: true,
            detail: {
              sessionToken: sessionToken,
              email: this.loginController?.user?.email || `${this.loginController?.user?.walletAddress || session?.wallet}@fanpass.wallet`,
              apiKey: this.apiKey,
              unlocked: true,
              mobilePass: session?.mobilePass ? session?.mobilePass : undefined,
              wallet: this.loginController?.user?.walletAddress
                ? this.loginController?.user?.walletAddress
                : session?.wallet,
              did: this.loginController?.user?.did?.token,
            },
          });
        }
      }
      this.loading = false;
    }
  }

  /**
   * Closes the overlay and updates the trigger button's aria-expanded attribute.
   *
   * @param e - The event object triggered by the action.
   */
  public closeOverlay(e: Event | null = null): void {
    const overlay = this.shadowRoot?.getElementById("fb-modal") as Overlay;
    const triggerButton = e?.target as Button;
    triggerButton?.setAttribute("aria-expanded", "false");
    overlay?.closeOverlay();
    this.emit(UnlockEvents.ModalClose, {
      bubbles: true,
      composed: true,
    })
  }

  /**
   * Opens the overlay and updates the claim state if the token status is null.
   *
   * @param e - The event that triggered the overlay to open.
   *
   * This method performs the following actions:
   * 1. Retrieves the overlay element from the shadow DOM.
   * 2. Sets the `aria-expanded` attribute of the trigger button to 'true'.
   * 3. If the token status is null, it sets the claim state to validating and calls `setTokenStatus` with the redeem token.
   * 4. Opens the overlay.
   */
  public openOverlay(e: Event | null = null): void {
    const overlay = this.shadowRoot?.getElementById("fb-modal") as Overlay;
    const triggerButton = e?.target as Button;
    triggerButton?.setAttribute("aria-expanded", "true");
    overlay?.openOverlay();
    if (overlay) {
      overlay.openOverlay();
      window.addEventListener("mousedown", this.onModalClickOut);
      this.emit(UnlockEvents.ModalOpen, {
        bubbles: true,
        composed: true,
      });
    }
  }

  private onModalClickOut = (evt: MouseEvent) => {
    // Start from the overlay element, not this.shadowRoot
    const overlay = this.shadowRoot?.getElementById(
      "fb-modal"
    ) as HTMLElement | null;
    if (!overlay) return;
    const boxNode = findBoxNode(overlay);
    if (!boxNode) return;
    if (!evt.composedPath().includes(boxNode)) {
      this.closeOverlay(evt);
      window.removeEventListener("mousedown", this.onModalClickOut);
    }
  };

  /**
   * Sets the page number for the pagination dots.
   * If no number param is given (e.g. this.setPageNumber()) the number will be set based on the current magic login state.
   * If number param is given (e.g. this.setPageNumber(4)) it will set page number to the number specified
   * @param number
   *
   */
  private setPageNumber(number?: number) {
    if (number !== undefined) {
      this._pageNumber = number;
      return;
    }

    const loginState = this._loginComponent?.getCurrentState();
    switch (loginState) {
      case LoginStateTypes.initialising:
      case LoginStateTypes.initialised:
        this._pageNumber = 1;
        break;
      case LoginStateTypes.requestCode:
      case LoginStateTypes.enterLoginCode:
      case LoginStateTypes.optCodeSubmited:
      case LoginStateTypes.isLoggedInLoading:
        this._pageNumber = 2;
        break;
      case LoginStateTypes.optFailed:
      case LoginStateTypes.isLoggedIn:
        this._pageNumber = 3;
        break;
      default:
        this._pageNumber = 1;
        break;
    }
  }

  /**
   * Logs a message to the console if debug logging is enabled.
   *
   * @param message - The message to log.
   * @param optionalParams - Additional optional parameters to log.
   */
  private logging(message?: any, ...optionalParams: any[]): void {
    if (this.debugLogging) {
      console.log(message, optionalParams);
    }
  }

  private renderContent(): TemplateResult {
    switch (this._unlockState) {
      case UnlockStateTypes.locked:
        return html`
          ${this.error
            ? html`
                <fp-error-screen
                  errorText=${this.error}
                  exportparts="error-screen-container,error-screen-message,error-screen-faq-link"
                >
                  <pu-button
                    exportparts="button,button:restart-button"
                    part="base-button base-restart-button"
                    slot="button"
                    type="button"
                    class="button"
                    name="disconnect"
                    size="medium"
                    variant="solid"
                  >
                    <pu-text
                      part="base-button-label base-restart-button-label"
                      exportparts="base:button-label,base:restart-button-label"
                      as="span"
                      variant="bodyMd"
                      fontWeight="regular"
                      withoutMargin
                      spacing="loose"
                    >
                      Start over
                    </pu-text>
                  </pu-button>
                </fp-error-screen>
              `
            : html`
                <fp-login
                  id="login"
                  buttonLabel="Submit"
                  logoSet=${JSON.stringify(this.logoSet)}
                  apiKey=${this.apiKey}
                  environment=${this.environment}
                  ?debugLogging=${this.debugLogging}
                  ?showLoaderAfterLogin=${false}
                  exportparts="${loginShadowParts}"
                >
                </fp-login>
              `}
        `;
      case UnlockStateTypes.unlocking:
        this.setPageNumber(2);
        return html`
          <pu-text
            as="p"
            variant="bodyLg"
            withoutMargin
            part="unlock-connecting-text"
          >
            ${this.connectingText.replace("${fanPassTitle}", this.fanPassTitle)}
          </pu-text>
          <svg
            part="loading__spinner"
            class="loading__spinner"
            width="16"
            height="16"
            xmlns="http://www.w3.org/2000/svg"
            focusable="false"
            role="presentation"
            aria-hidden="true"
            viewBox="0 0 512 512"
          >
            <path
              d="M463.9 376c7.6 4.4 17.5 1.8 21.4-6.1c17.1-34.3 26.7-73 26.7-113.9C512 120 405.9 8.8 272 .5c-8.8-.5-16 6.7-16 15.5v0c0 8.8 7.2 15.9 16 16.6C388.2 40.8 480 137.7 480 256c0 35.1-8.1 68.3-22.5 97.9c-3.9 8-1.3 17.7 6.4 22.2v0z"
            />
          </svg>
        `;
      case UnlockStateTypes.unlocked:
        this.setPageNumber(3);
        return html`
          <pu-text as="p" variant="bodyLg" withoutMargin>
            You are connected!
          </pu-text>
          <pu-text as="p" variant="bodyMd" withoutMargin>
            Email used: ${this.loginController?.user?.email}
          </pu-text>
          <pu-button
            exportparts="button,button:disconnect-button"
            part="base-button base-disconnect-button"
            @click="${this.handleLogout}"
            type="button"
            class="button"
            name="disconnect"
            size="medium"
            variant="solid"
          >
            <pu-text
              exportparts="base:button-label,base:disconnect-button-label"
              part="base-button-label base-disconnect-button-label"
              as="span"
              variant="bodyMd"
              fontWeight="regular"
              withoutMargin
              spacing="loose"
            >
              Disconnect
            </pu-text>
          </pu-button>
        `;
      case UnlockStateTypes.lockedLoggingIn:
        this.setPageNumber(3);
        return html`
          <fp-error-screen
            errorText="You don't seem to have a valid ${this.fanPassTitle}"
          >
            <pu-button
              exportparts="button,button:restart-button"
              part="base-button base-restart-button"
              slot="button"
              @click=${this.handleLogout}
              type="button"
              class="button"
              name="disconnect"
              size="medium"
              variant="solid"
            >
              <pu-text
                exportparts="base:button-label,base:restart-button-label"
                part="base-button-label base-restart-button-label"
                as="span"
                variant="bodyMd"
                fontWeight="regular"
                withoutMargin
                spacing="loose"
              >
                Start over
              </pu-text>
            </pu-button>
          </fp-error-screen>
        `;
      default:
        this.setPageNumber(3);
        return html`
          <fp-error-screen errorText="Something went wrong">
            <pu-button
              exportparts="button,button:restart-button"
              part="base-button base-restart-button"
              slot="button"
              @click=${this.handleLogout}
              type="button"
              class="button"
              name="disconnect"
              size="medium"
              variant="solid"
            >
              <pu-text
                exportparts="base:button-label,base:restart-button-label"
                part="base-button-label base-restart-button-label"
                as="span"
                variant="bodyMd"
                fontWeight="regular"
                withoutMargin
                spacing="loose"
              >
                Start over
              </pu-text>
            </pu-button>
          </fp-error-screen>
        `;
    }
  }

  private connectedRender(content: string = ""): TemplateResult {
    if (this.hideConnected) {
      return html``;
    }
    return html`
      <fp-connected
        exportparts="${connectedShadowParts}"
        apiKey=${this.apiKey}
        fanPassTitle=${this.fanPassTitle}
        displayText=${this._unlockState === UnlockStateTypes.unlocked
          ? "You're connected!"
          : `You don't have a valid ${this.fanPassTitle}`}
        ?showMobilePassButtons=${false}
        environment=${this.environment}
        ?debugLogging=${this.debugLogging}
        ?ownsFp=${this._unlockState === UnlockStateTypes.unlocked
          ? true
          : false}
      >
        <div part="form-content" slot="form-content">
          <form part="email-signup-form">
            <label part="label">
              <input
                part="input"
                type="checkbox"
                name="emailSignup"
                value="true"
              />
              Sign up for email updates
            </label>
          </form>
        </div>
        <slot>${content}</slot>
      </fp-connected>
    `;
  }

  private renderConnectButton(): TemplateResult {
    if (
      this._unlockState === UnlockStateTypes.unlocked
      || this.hideUnlockButton
    ) {
      return html``;
    }
    return html`<pu-button
      exportparts="button,button:connect-button"
      part="base-button base-connect-button"
      class="button"
      @click=${this.openOverlay}
      type="button"
      name="unlock"
      size="medium"
      ?disabled=${this.loading ||
      this.loginController?.loginInit === false ||
      (!this.walletCheckResponse && this.loginController?.loggedIn)}
      ?loading=${this.loginController?.loginInit === false ||
      (!this.walletCheckResponse && this.loginController?.loggedIn)}
      variant="solid"
    >
      <pu-text
        exportparts="base:button-label,base:connect-button-label"
        part="base-button-label base-connect-button-label"
        as="span"
        variant="bodyMd"
        fontWeight="regular"
        withoutMargin
        spacing="loose"
      >
        ${this.buttonLabel}
      </pu-text>
    </pu-button>`;
  }

  public render(): TemplateResult {
    this.setPageNumber();
    if (
      this._unlockState === UnlockStateTypes.unlocked ||
      this.walletCheckResponse
    ) {
      return this.connectedRender();
    }

    if (this.inline) {
      return html`
        <fp-frame
          fanPassTitle=${this.fanPassTitle}
          maxPages=${3}
          currentPageNumber=${this._pageNumber}
          .logoSet=${this.logoSet}
          logoUrl=${this.logoUrl}
          exportparts="${frameShadowParts}"
        >
          <pu-text as="p" variant="bodyLg" withoutMargin part="unlock-title">
            ${this.title.replace("${fanPassTitle}", this.fanPassTitle)}
          </pu-text>
          ${this.renderContent()}
        </fp-frame>
      `;
    }

    return html`
      ${this.cta && !this.hideUnlockButton
        ? html`
            <pu-horizontal-stack
              spacing="24"
              align="center"
              alignNarrow="center"
              justify="center"
              justifyNarrow="center"
              class="cta"
            >
              <pu-text
                as="span"
                variant="bodyMd"
                fontWeight="regular"
                withoutMargin
                spacing="loose"
              >
                ${this.ctaText}
              </pu-text>
              ${this.renderConnectButton()}
            </pu-horizontal-stack>
          `
        : this.renderConnectButton()}
      <pu-overlay id="fb-modal" class="fb-modal">
        <pu-box
          slot="content"
          class="modalContentBox"
          padding=${100}
          paddingNarrow="60, 0"
        >
          <button
            part="button-icon"
            style="position: absolute; top: 1em; right: 1em; z-index: 501"
            class="button-icon"
            type="button"
            aria-label="Close modal"
            aria-controls="overlay"
            @click=${this.closeOverlay}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              id="close"
              part="icon"
              class="icon"
              width="1em"
              height="1em"
              viewBox="0 0 384 512"
              role="presentation"
              aria-hidden="true"
              focusable="false"
              fill="currentColor"
            >
              <path
                d="M192 233.4L59.5 100.9 36.9 123.5 169.4 256 36.9 388.5l22.6 22.6L192 278.6 324.5 411.1l22.6-22.6L214.6 256 347.1 123.5l-22.6-22.6L192 233.4z"
              />
            </svg>
          </button>
          <fp-frame
            exportparts="${frameShadowParts}"
            fanPassTitle=${this.fanPassTitle}
            maxPages=${3}
            currentPageNumber=${this._pageNumber}
            .logoSet=${this.logoSet}
            logoUrl=${this.logoUrl}
          >
            <pu-text as="p" variant="bodyLg" withoutMargin part="unlock-title">
              ${this.title.replace("${fanPassTitle}", this.fanPassTitle)}
            </pu-text>
            ${this.renderContent()}
          </fp-frame>
        </pu-box>
      </pu-overlay>
    `;
  }
}
